// Crash reporter (spec section 1): window errors, unhandled rejections and Vue's errorHandler go to the
// platform's POST /api/client-crashes. Stored in localStorage FIRST, sent after, resent on the next load.
// On for every tenant (not behind ux_analytics). Nothing here may throw into the app.
import { scrubMessage, scrubStack, normalizeStack } from './scrub.mjs';
import { enqueue, readQueue, prune, writeQueue, remove } from './crash-queue.mjs';

export const CRASH_URL = 'https://support.accutrax.id/api/client-crashes';

function safeStorage() {
  try {
    const s = window.localStorage;
    s.getItem('x');
    return s;
  } catch {
    const m = new Map();
    return { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => m.set(k, v), removeItem: (k) => m.delete(k) };
  }
}

function browserInfo() {
  try {
    const ua = navigator.userAgent || '';
    const os =
      (/Windows NT ([\d.]+)/.exec(ua) && `Windows NT ${RegExp.$1}`) ||
      (/Android ([\d.]+)/.exec(ua) && `Android ${RegExp.$1}`) ||
      (/Mac OS X ([\d_]+)/.exec(ua) && `macOS ${RegExp.$1.replace(/_/g, '.')}`) ||
      (/CrOS/.test(ua) && 'ChromeOS') ||
      (/Linux/.test(ua) && 'Linux') ||
      '';
    const br =
      (/Edg\/(\d+)/.exec(ua) && `Edge ${RegExp.$1}`) ||
      (/OPR\/(\d+)/.exec(ua) && `Opera ${RegExp.$1}`) ||
      (/Chrome\/(\d+)/.exec(ua) && `Chrome ${RegExp.$1}`) ||
      (/Firefox\/(\d+)/.exec(ua) && `Firefox ${RegExp.$1}`) ||
      (/Version\/(\d+).*Safari/.exec(ua) && `Safari ${RegExp.$1}`) ||
      'browser';
    const mobile = /Mobi|Android/.test(ua) ? ' mobile' : '';
    return { os_version: os, device_model: `${br}${mobile}` };
  } catch {
    return { os_version: '', device_model: '' };
  }
}

// ctx: { app, appVersionName, appVersionCode, getTenant(), getScreen(), getBreadcrumbs(), enabled() }
export function createCrashReporter(ctx) {
  const storage = safeStorage();
  const sending = new Set();

  async function sendEntry(entry) {
    if (sending.has(entry.id) || !ctx.enabled()) return;
    sending.add(entry.id);
    try {
      const res = await fetch(CRASH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(entry.report),
        keepalive: true,
        credentials: 'omit',
      });
      // 2xx: delivered. 4xx other than 429: the platform will never take it, drop it too.
      if (res.ok || (res.status >= 400 && res.status < 500 && res.status !== 429)) remove(storage, entry.id);
    } catch {
      /* offline: stays queued for the next load */
    } finally {
      sending.delete(entry.id);
    }
  }

  function resendQueue() {
    try {
      const q = prune(readQueue(storage), Date.now());
      writeQueue(storage, q);
      q.forEach((e) => sendEntry(e));
    } catch {
      /* never throw */
    }
  }

  function report(err, source = 'error') {
    try {
      // API refusals and network errors are shown to the user already; they are not crashes.
      if (isExpected(err)) return null;
      let e = err;
      if (!(e instanceof Error)) {
        const text = typeof e === 'string' ? e : (e && (e.message || e.reason)) || safeJson(e) || String(e);
        e = { name: source === 'unhandledrejection' ? 'UnhandledRejection' : 'Error', message: text, stack: e?.stack };
      }
      const info = browserInfo();
      const rep = {
        exception_class: String(e.name || e.constructor?.name || 'Error').slice(0, 120),
        message: scrubMessage(e.message || ''),
        stack: scrubStack(normalizeStack(e.stack, `${source} (${location.pathname}${location.hash.split('?')[0]})`)),
        platform: 'web',
        app: ctx.app,
        tenant: ctx.getTenant(),
        app_version_name: ctx.appVersionName,
        app_version_code: ctx.appVersionCode,
        os_version: info.os_version,
        device_model: info.device_model,
        screen: String(ctx.getScreen() || '').slice(0, 120),
        breadcrumbs: ctx.getBreadcrumbs().slice(-10),
        occurred_at: new Date().toISOString(),
      };
      if (!rep.stack) rep.stack = `at ${source}`;
      const entry = enqueue(storage, rep);
      if (entry) sendEntry(entry);
      return entry;
    } catch {
      return null;
    }
  }

  function install(app) {
    try {
      window.addEventListener('error', (ev) => {
        // Resource load errors (img/script) have no .error and target an element: not a crash.
        if (!ev || (ev.target && ev.target !== window)) return;
        report(ev.error || { name: 'Error', message: ev.message, stack: ev.filename ? `at ${ev.filename}:${ev.lineno}:${ev.colno}` : '' }, 'window.onerror');
      });
      window.addEventListener('unhandledrejection', (ev) => {
        const r = ev?.reason;
        report(r, 'unhandledrejection');
      });
      if (app) {
        const prev = app.config.errorHandler;
        app.config.errorHandler = (err, instance, infoStr) => {
          console.error(err); // Vue stops logging once a handler is set
          report(err, `vue:${infoStr || ''}`);
          if (prev) {
            try {
              prev(err, instance, infoStr);
            } catch {
              /* ignore */
            }
          }
        };
      }
      setTimeout(resendQueue, 3000);
    } catch {
      /* never throw */
    }
  }

  return { install, report, resendQueue };
}

function safeJson(v) {
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
}

function isExpected(e) {
  return !!(e && typeof e === 'object' && (e.isAxiosError || e.isApiError || e.name === 'AxiosError' || e.name === 'CanceledError'));
}
